"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "./Button";
import { Download, Smartphone, Monitor, X, Check } from "lucide-react";

// Extend Navigator interface for iOS standalone mode
declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWADownloadToastProps {
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center";
  autoShow?: boolean;
  delay?: number; // Delay in milliseconds before showing
  onInstall?: () => void;
  onDismiss?: () => void;
}

export function PWADownloadToast({
  position = "top-right",
  autoShow = true,
  delay = 3000,
  onInstall,
  onDismiss,
}: PWADownloadToastProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);
  const installPromptReadyRef = useRef(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Guard against SSR
    if (typeof window === 'undefined') return;
    let isMounted = true;

    // Check if app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia("(display-mode: standalone)").matches) {
        setIsInstalled(true);
        return true;
      }
      
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        return true;
      }
      
      return false;
    };

    if (checkIfInstalled()) {
      return;
    }

    const detectIosSafari = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
      const isSafariBrowser = userAgent.includes('safari') && !userAgent.includes('crios') && !userAgent.includes('fxios');
      return isIosDevice && isSafariBrowser;
    };

    // Check if toast was dismissed recently
    const checkDismissed = () => {
      if (typeof window === 'undefined') return false;
      const dismissedTime = localStorage.getItem("pwa-toast-dismissed");
      if (dismissedTime) {
        const dismissedDate = new Date(dismissedTime);
        const now = new Date();
        if (now < dismissedDate) {
          return true; // Don't show if dismissed recently
        }
      }
      return false;
    };

    // Check if PWA is supported
    // Note: beforeinstallprompt is only available on Chrome/Edge/Opera/Chrome-based browsers
    // iOS Safari doesn't support it, but we can still show manual install instructions
    const checkSupport = async () => {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return;
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!isMounted) return;
        const iosSafari = detectIosSafari();
        setIsIos(iosSafari);
        if (registration || iosSafari) {
          setIsSupported(true);
        }
      } catch (error) {
        console.error("Error checking service worker support:", error);
      }
    };

    checkSupport();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      if (!isMounted) return;

      setDeferredPrompt(e as BeforeInstallPromptEvent);
      installPromptReadyRef.current = true;
      setIsSupported(true);
      
      // Show toast after delay if autoShow is enabled and not dismissed
      if (autoShow && !checkDismissed()) {
        setTimeout(() => {
          if (isMounted) {
            setShowToast(true);
          }
        }, delay);
      }
    };

    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      if (!isMounted) return;

      setIsInstalled(true);
      setDeferredPrompt(null);
      installPromptReadyRef.current = false;
      setShowToast(false);
      setInstallSuccess(true);
      
      // Show success toast briefly
      setTimeout(() => {
        if (isMounted) {
          setInstallSuccess(false);
        }
      }, 3000);
    };

    // Listen for custom show toast event
    const handleShowToastEvent = () => {
      if (installPromptReadyRef.current) {
        setShowToast(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("show-pwa-toast", handleShowToastEvent);

    if (autoShow && !checkDismissed() && detectIosSafari()) {
      setTimeout(() => {
        if (isMounted) {
          setShowToast(true);
        }
      }, delay);
    }

    return () => {
      isMounted = false;
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("show-pwa-toast", handleShowToastEvent);
    };
  }, [autoShow, delay]);

  useEffect(() => {
    installPromptReadyRef.current = !!deferredPrompt;
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    // If we have a deferred prompt, use it
    if (deferredPrompt) {
      setIsInstalling(true);
      
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === "accepted") {
          onInstall?.();
        } else {
          setShowToast(false);
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        console.error("Error showing install prompt:", error);
        setShowToast(false);
      } finally {
        setIsInstalling(false);
      }
      return;
    }

    // Fallback: If no deferred prompt (e.g., iOS Safari, or event didn't fire),
    // we can't programmatically trigger install, but we can provide instructions
    // For now, just dismiss the toast since manual install instructions
    // would be better handled in a separate component or modal
    setShowToast(false);
  };

  const handleDismiss = () => {
    setShowToast(false);
    onDismiss?.();
    
    // Don't show again for 24 hours
    if (typeof window !== 'undefined') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      localStorage.setItem("pwa-toast-dismissed", tomorrow.toISOString());
    }
  };


  // Check if dismissed (check localStorage directly to avoid race conditions)
  const isCurrentlyDismissed = (() => {
    if (typeof window === 'undefined') return false;
    const dismissedTime = localStorage.getItem("pwa-toast-dismissed");
    if (dismissedTime) {
      const dismissedDate = new Date(dismissedTime);
      const now = new Date();
      return now < dismissedDate;
    }
    return false;
  })();

  // Success toast
  if (installSuccess) {
    return (
      <div className={`fixed z-50 ${getPositionClasses(position)}`}>
        <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-4 max-w-sm">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800">
                App Installed!
              </h3>
              <p className="text-xs text-green-600 mt-1">
                Nomu has been added to your device
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const showManualInstall = isIos && !deferredPrompt;

  // Don't show if already installed, not supported, dismissed, or not showing
  if (isInstalled || !isSupported || !showToast || isCurrentlyDismissed) {
    return null;
  }

  // Don't show if prompt isn't ready and manual install isn't available
  if (!deferredPrompt && !showManualInstall) {
    return null;
  }

  return (
    <div className={`fixed z-50 ${getPositionClasses(position)}`}>
      <div className="bg-card border border-border rounded-lg shadow-lg p-4 max-w-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center">
                <Download className="h-5 w-5 text-brand" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-card-fg">
                Install Nomu
              </h3>
              <p className="text-xs text-muted-fg mt-1">
                Get quick access and offline functionality
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 rounded-md hover:bg-bg-hover transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4 text-muted-fg" />
          </button>
        </div>
        
        <div className="flex space-x-2 mt-3">
          {showManualInstall ? (
            <Button
              onClick={handleDismiss}
              size="sm"
              className="flex-1"
            >
              Got it
            </Button>
          ) : (
            <>
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="flex-1"
                disabled={isInstalling}
              >
                {isInstalling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Installing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Install
                  </>
                )}
              </Button>
              <Button
                onClick={handleDismiss}
                variant="outline"
                size="sm"
                disabled={isInstalling}
              >
                Later
              </Button>
            </>
          )}
        </div>

        {/* Platform-specific instructions */}
        <div className="mt-3 pt-3 border-t border-border">
          {showManualInstall ? (
            <div className="text-xs text-muted-fg">
              On iOS: tap Share, then “Add to Home Screen”.
            </div>
          ) : (
            <div className="flex items-center space-x-4 text-xs text-muted-fg">
              <div className="flex items-center space-x-1">
                <Smartphone className="h-3 w-3" />
                <span>Mobile</span>
              </div>
              <div className="flex items-center space-x-1">
                <Monitor className="h-3 w-3" />
                <span>Desktop</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getPositionClasses(position: string) {
  switch (position) {
    case "top-right":
      return "top-4 right-4";
    case "top-left":
      return "top-4 left-4";
    case "bottom-right":
      return "bottom-4 right-4";
    case "bottom-left":
      return "bottom-4 left-4";
    case "top-center":
      return "top-4 left-1/2 transform -translate-x-1/2";
    case "bottom-center":
      return "bottom-4 left-1/2 transform -translate-x-1/2";
    default:
      return "top-4 right-4";
  }
}

// Hook to manually trigger the toast
export function usePWADownloadToast() {
  const showToast = () => {
    // This would be implemented with a ref to the toast component
    // For now, we'll use a simple approach
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('show-pwa-toast');
      window.dispatchEvent(event);
    }
  };

  return { showToast };
}
